import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

import { ASSETS, NAV_PUBLIC } from "@/lib/company";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="public-header"
      className={`fixed top-0 inset-x-0 z-50 bg-ink-black border-b transition-shadow duration-300 ${
        scrolled
          ? "border-white/10 shadow-[0_18px_45px_rgba(0,0,0,0.45)]"
          : "border-white/6"
      }`}
      style={{
        backgroundImage: "none",
      }}
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-[96px] md:h-[112px] flex items-center justify-between">
        <Link
          to="/"
          aria-label="Minga Architecture"
          data-testid="header-logo"
          className="flex items-center h-full shrink-0"
        >
          <img
            src={ASSETS.logo}
            alt="Minga Architecture"
            className="h-[47px] md:h-[55px] lg:h-[62px] w-auto max-w-[200px] md:max-w-[265px] object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_PUBLIC.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              data-testid={`nav-${item.href.replace(/\W/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[13px] tracking-[0.14em] uppercase font-medium transition-colors duration-300 ${
                  isActive
                    ? "text-brand-yellow"
                    : "text-brand-white/90 hover:text-brand-yellow"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}

                  {isActive && (
                    <span className="absolute -bottom-2 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-brand-yellow" />
                  )}
                </>
              )}
            </NavLink>
          ))}

        </nav>

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="lg:hidden grid place-items-center h-11 w-11 -mr-2 text-brand-white"
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden fixed inset-x-0 top-[96px] md:top-[112px] bottom-0 bg-ink-black transition-all duration-500 ${
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-3 pointer-events-none"
        }`}
        style={{
          backgroundImage: "none",
        }}
      >
        <div className="flex h-full flex-col px-6 pt-10 pb-16">
          <ul className="flex flex-col gap-6">
            {NAV_PUBLIC.map((item) => (
              <li key={item.href}>
                <NavLink
                  to={item.href}
                  className="text-3xl md:text-4xl font-semibold tracking-tight text-brand-white transition-colors hover:text-brand-yellow"
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="mt-auto flex flex-col gap-3">
            <p className="text-xs uppercase tracking-[0.24em] text-brand-gray">
              Seattle, WA · Quito, Ecuador
            </p>
          </div>
        </div>
      </div>
    </header>
  );
}